import React, { useState } from 'react';
import PropTypes from 'prop-types';

import icInfoDark from 'core/assets/svg/ic-info-dark-16.svg';
import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { TextField } from 'core/components/text-field';
import { useToast } from 'core/components/toast';
import { DRAFT_STATUS, getDraftStatus } from 'core/helpers/policy-drafting';
import { useStore } from 'core/store';
import useStyles from './policy-draft-delete-modal.styles';

const PolicyDraftDeleteModal = ({ onClose, onDeleted, policyId, preview }) => {
  const classes = useStyles();
  const toast = useToast();
  const {
    account: {
      policies: {
        policy: { deletePolicyPreviewDraft }
      }
    }
  } = useStore();
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const isWorkingDraft = getDraftStatus(preview) === DRAFT_STATUS.WORKING;
  const trimmedReason = reason.trim();
  const canDelete = isWorkingDraft || trimmedReason.length > 0;

  const handleDelete = async () => {
    setDeleting(true);
    const { ok, message } = await deletePolicyPreviewDraft({
      policyId,
      previewId: preview.id,
      note: trimmedReason || undefined
    });
    setDeleting(false);
    if (!ok) {
      toast.notify({ type: 'error', message: message ?? 'The draft could not be deleted. Try again.' });
      return;
    }
    toast.notify({ type: 'success', message: 'Draft deleted' });
    onDeleted();
  };

  return (
    <BaseDialog size="sm" open onClose={onClose} className={classes.dialog}>
      <Label type="h1">{isWorkingDraft ? 'Delete this working draft?' : 'Delete this draft?'}</Label>
      <Label type="darkGreenBig" className={classes.body}>
        {isWorkingDraft
          ? "This working draft will be removed and you won't be able to come back to it."
          : 'The draft will be removed, and the policy will become editable again. The draft owner will be notified.'}
      </Label>
      {!isWorkingDraft && (
        <TextField
          id="draftDeleteReason"
          label="Reason for deleting"
          mode="light"
          multiline
          rows={4}
          fullWidth
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className={classes.reason}
        />
      )}
      <div className={classes.footer}>
        <div className={classes.warning}>
          <img src={icInfoDark} alt="" />
          <Label type="body3" className={classes.warningText}>
            You will not be able to undo this action.
          </Label>
        </div>
        <div className={classes.actions}>
          <Button variant="contained" color="secondary" onClick={onClose} className={classes.actionButton}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDelete}
            disabled={!canDelete}
            loading={deleting}
            className={classes.actionButton}
          >
            Delete
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

PolicyDraftDeleteModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
  policyId: PropTypes.string.isRequired,
  preview: PropTypes.object.isRequired
};

export default PolicyDraftDeleteModal;
